import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  finishTrainSkill,
  getNextTrainTime,
  getTrainRequest,
  requestTrainSkill,
  type TrainRequest,
} from "@/helper/contractFunctions/MafiaKillSkill";
import { isIndexingTxError } from "@/helper/parseTxError";

export interface KillSkillState {
  isTrainingKill: boolean;
  nextTrainTime: number;
  trainRequest: TrainRequest;
}

const emptyTrainRequest: TrainRequest = {
  isPending: false,
  trainType: 0,
  requestBlock: 0,
};

const initialState: KillSkillState = {
  isTrainingKill: false,
  nextTrainTime: -1,
  trainRequest: emptyTrainRequest,
};

export const requestTrainSkillAction = createAsyncThunk(
  "killSkill/requestTrainSkillAction",
  async ({ account, trainType }: { account: string; trainType: number }) =>
    requestTrainSkill(account, trainType)
);

export const finishTrainSkillAction = createAsyncThunk(
  "killSkill/finishTrainSkillAction",
  async ({ account }: { account: string }) => finishTrainSkill(account)
);

export const getNextTrainTimeAction = createAsyncThunk(
  "killSkill/getNextTrainTimeAction",
  getNextTrainTime
);

export const getTrainRequestAction = createAsyncThunk(
  "killSkill/getTrainRequestAction",
  getTrainRequest
);

const killSkillSlice = createSlice({
  name: "killSkill",
  initialState,
  reducers: {
    startTrain: (state) => {
      state.isTrainingKill = true;
    },
    finishTrain: (state) => {
      state.isTrainingKill = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestTrainSkillAction.pending, (state) => {
        state.isTrainingKill = true;
      })
      .addCase(requestTrainSkillAction.fulfilled, (state, { payload }) => {
        state.trainRequest = {
          isPending: true,
          trainType: payload.trainType,
          requestBlock: payload.requestBlock,
        };
      })
      .addCase(requestTrainSkillAction.rejected, (state, { error }) => {
        if (!isIndexingTxError(error)) {
          state.isTrainingKill = false;
        }
      })
      .addCase(finishTrainSkillAction.pending, (state) => {
        state.isTrainingKill = true;
      })
      .addCase(finishTrainSkillAction.fulfilled, (state) => {
        state.isTrainingKill = false;
        state.trainRequest = emptyTrainRequest;
      })
      .addCase(finishTrainSkillAction.rejected, (state, { error }) => {
        if (!isIndexingTxError(error)) {
          state.isTrainingKill = false;
        }
      })
      .addCase(getNextTrainTimeAction.fulfilled, (state, { payload }) => {
        state.nextTrainTime = payload;
      })
      .addCase(getNextTrainTimeAction.rejected, (state) => {
        state.nextTrainTime = -1;
      })
      .addCase(getTrainRequestAction.fulfilled, (state, { payload }) => {
        state.trainRequest = payload;
      })
      .addCase(getTrainRequestAction.rejected, (state) => {
        state.trainRequest = emptyTrainRequest;
      });
  },
});

export const { startTrain, finishTrain } = killSkillSlice.actions;
export default killSkillSlice.reducer;
